import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import {
    CheckCircle2,
    FileText,
    PackageSearch,
    ShieldAlert,
    ShieldCheck,
    Smartphone,
    Users
} from 'lucide-react'
import { cn } from '@/lib/utils'
import SectionHeading from '@/components/landing/section-heading'

interface FeatureTileProps {
    icon: LucideIcon
    title: string
    description: string
    className?: string
    children: ReactNode
}

const FeatureTile: FC<FeatureTileProps> = ({ icon: Icon, title, description, className, children }): ReactNode => {
    return (
        <article className={cn('reveal flex flex-col rounded-3xl border border-border bg-white p-6 sm:p-8', className)}>
            <span className='grid size-11 place-items-center rounded-2xl bg-brand-50 text-brand-600 ring-1 ring-brand-100'>
                <Icon className='size-5' />
            </span>

            <h3 className='mt-5 font-heading text-xl font-bold tracking-tight text-foreground'>
                {title}
            </h3>

            <p className='mt-2 max-w-lg text-base leading-relaxed text-muted-foreground'>
                {description}
            </p>

            <div aria-hidden='true' className='mt-7 flex flex-1 flex-col justify-end'>
                <div className='rounded-2xl border border-border bg-background p-4 sm:p-5'>
                    {children}
                </div>
            </div>
        </article>
    )
}

const QUOTE_TOTALS = [
    { label: "Draft", count: 1, value: "₹2,10,000", tone: 'bg-slate-400' },
    { label: "Sent", count: 2, value: "₹2,11,250", tone: 'bg-primary' },
    { label: "Accepted", count: 2, value: "₹1,10,500", tone: 'bg-emerald-500' }
]

const RECENT_QUOTES = [
    { number: 'QT-2042', customer: "Kapoor Electricals", amount: "₹86,750", status: "Sent", tone: 'bg-brand-50 text-brand-700 ring-brand-100' },
    { number: 'QT-2038', customer: "Verma & Sons", amount: "₹62,300", status: "Accepted", tone: 'bg-emerald-50 text-emerald-700 ring-emerald-100' }
]

const QuotesVisual: FC = (): ReactNode => {
    return (
        <div>
            <div className='grid grid-cols-3 gap-2 sm:gap-3'>
                {QUOTE_TOTALS.map(total => (
                    <div key={total.label} className='rounded-xl border border-border bg-white p-3'>
                        <div className='flex items-center gap-1.5'>
                            <span className={cn('size-2 rounded-full', total.tone)} />
                            <p className='text-xs font-medium text-muted-foreground'>
                                {total.label}
                            </p>
                        </div>

                        <p className='mt-1.5 font-heading text-xl font-bold text-foreground tabular-nums'>
                            {total.count}
                        </p>

                        <p className='truncate text-[11px] text-muted-foreground tabular-nums'>
                            {total.value}
                        </p>
                    </div>
                ))}
            </div>

            <ul className='mt-3 space-y-2'>
                {RECENT_QUOTES.map(quote => (
                    <li key={quote.number} className='flex items-center justify-between gap-3 rounded-xl border border-border bg-white px-3 py-2.5'>
                        <div className='flex min-w-0 items-center gap-3'>
                            <span className='hidden font-mono text-[11px] text-muted-foreground sm:inline'>
                                {quote.number}
                            </span>

                            <span className='truncate text-sm font-medium text-foreground'>
                                {quote.customer}
                            </span>
                        </div>

                        <div className='flex shrink-0 items-center gap-2'>
                            <span className='text-sm font-semibold text-foreground tabular-nums'>
                                {quote.amount}
                            </span>

                            <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1', quote.tone)}>
                                {quote.status}
                            </span>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    )
}

const TIMELINE = [
    { time: "9:02", name: "Priya S.", role: "Sales" },
    { time: "9:14", name: "Arjun M.", role: "Warehouse" },
    { time: "9:31", name: "Neha R.", role: "Accounts" }
]

const SignInsVisual: FC = (): ReactNode => {
    return (
        <div>
            <ol className='relative space-y-4 before:absolute before:bottom-2 before:left-[calc(3.5625rem_-_0.5px)] before:top-2 before:w-px before:bg-border'>
                {TIMELINE.map(entry => (
                    <li key={entry.name} className='relative flex items-center gap-3'>
                        <span className='w-10 shrink-0 text-right text-xs font-semibold text-muted-foreground tabular-nums'>
                            {entry.time}
                        </span>

                        <span className='relative z-10 size-2.5 shrink-0 rounded-full bg-primary ring-4 ring-background' />

                        <div className='min-w-0'>
                            <p className='truncate text-sm font-semibold text-foreground'>
                                {entry.name}
                            </p>

                            <p className='text-[11px] text-muted-foreground'>
                                {entry.role}
                            </p>
                        </div>
                    </li>
                ))}
            </ol>

            <div className='mt-4 flex items-center justify-between rounded-xl bg-white px-3 py-2 ring-1 ring-border'>
                <span className='text-xs font-medium text-muted-foreground'>
                    Signed in today
                </span>

                <span className='text-sm font-bold text-foreground tabular-nums'>
                    8 of 11
                </span>
            </div>
        </div>
    )
}

const STOCK = [
    { item: "MCB 32A double pole", left: 6, level: 'w-[14%]', tone: 'bg-rose-500' },
    { item: "LED panel 18W", left: 14, level: 'w-[30%]', tone: 'bg-amber-500' },
    { item: "PVC conduit 25 mm", left: 22, level: 'w-[42%]', tone: 'bg-amber-400' }
]

const StockVisual: FC = (): ReactNode => {
    return (
        <div>
            <p className='text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground'>
                Below reorder level
            </p>

            <ul className='mt-3 space-y-3.5'>
                {STOCK.map(stock => (
                    <li key={stock.item}>
                        <div className='flex items-center justify-between gap-3'>
                            <span className='truncate text-sm font-medium text-foreground'>
                                {stock.item}
                            </span>

                            <span className='shrink-0 text-xs font-semibold text-muted-foreground tabular-nums'>
                                {stock.left} left
                            </span>
                        </div>

                        <div className='mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted'>
                            <div className={cn('h-full rounded-full', stock.level, stock.tone)} />
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    )
}

const ALERTS = [
    { icon: ShieldAlert, title: "3 wrong PIN attempts", detail: "Counter tablet · 10:14 AM", badge: "High", tone: 'bg-rose-50 text-rose-600', badgeTone: 'bg-rose-50 text-rose-700 ring-rose-100' },
    { icon: Smartphone, title: "Sign-in from a new device", detail: "Arjun M. · 9:14 AM", badge: "Review", tone: 'bg-amber-50 text-amber-700', badgeTone: 'bg-amber-50 text-amber-800 ring-amber-100' },
    { icon: ShieldCheck, title: "No other unusual activity today", detail: "Everything else looks normal", badge: "OK", tone: 'bg-emerald-50 text-emerald-600', badgeTone: 'bg-emerald-50 text-emerald-700 ring-emerald-100' }
]

const AlertsVisual: FC = (): ReactNode => {
    return (
        <ul className='space-y-2'>
            {ALERTS.map(alert => (
                <li key={alert.title} className='flex items-center gap-3 rounded-xl border border-border bg-white p-3'>
                    <span className={cn('grid size-9 shrink-0 place-items-center rounded-lg', alert.tone)}>
                        <alert.icon className='size-4' />
                    </span>

                    <div className='min-w-0 flex-1'>
                        <p className='truncate text-sm font-semibold text-foreground'>
                            {alert.title}
                        </p>

                        <p className='truncate text-xs text-muted-foreground'>
                            {alert.detail}
                        </p>
                    </div>

                    <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1', alert.badgeTone)}>
                        {alert.badge}
                    </span>
                </li>
            ))}
        </ul>
    )
}

const FeaturesSection: FC = (): ReactNode => {
    return (
        <section id='features' aria-labelledby='features-title' className='py-20 sm:py-28'>
            <div className='mx-auto max-w-6xl px-5 sm:px-6 lg:px-8'>
                <SectionHeading
                    id='features-title'
                    align='center'
                    eyebrow="Features"
                    title="Four things every owner checks. One screen."
                    description="Elenix opens on Today, a summary of what's happening across your business, so the important things find you."
                />

                <div className='mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3'>
                    <FeatureTile
                        icon={FileText}
                        className='lg:col-span-2'
                        title="Today's quotations"
                        description="Every quote your team raises, with the customer, the value and where it stands, from draft to accepted."
                    >
                        <QuotesVisual />
                    </FeatureTile>

                    <FeatureTile
                        icon={Users}
                        title="Who signed in"
                        description="See who has started work and when. Every sign-in belongs to a person's own mobile number."
                    >
                        <SignInsVisual />
                    </FeatureTile>

                    <FeatureTile
                        icon={PackageSearch}
                        title="Low stock"
                        description="Items running low rise to the top of your day, so you can reorder before a customer hears “out of stock”."
                    >
                        <StockVisual />
                    </FeatureTile>

                    <FeatureTile
                        icon={ShieldAlert}
                        className='lg:col-span-2'
                        title="Security alerts"
                        description="Repeated wrong PINs and sign-ins from new devices are flagged on your home screen, so nothing slips past you."
                    >
                        <AlertsVisual />
                    </FeatureTile>
                </div>

                <p className='reveal mx-auto mt-10 flex max-w-xl items-center justify-center gap-2 text-center text-sm text-muted-foreground'>
                    <CheckCircle2 className='size-4 shrink-0 text-emerald-600' />
                    All four live on the Today screen, the first thing you see when you sign in.
                </p>
            </div>
        </section>
    )
}

export default FeaturesSection
